import { Children, cloneElement, type ReactElement } from 'react';
import { Stack, Text, useDirection } from '@adc/parallax-component-library';
import type { AccessibilityProps } from '@adc/parallax-component-library/types/types';
import type { IconProps } from '@adc/parallax-icons';
import { styled } from '@tamagui/core';
import { useNavigate, useRouterState } from '@tanstack/react-router';

import type { FileRouteTypes } from '~/routeTree.gen';

export type NavigationItem = {
  /**
   * The label for the navigation item
   */
  label: string;

  /**
   * The path to navigate to when the item is clicked
   */
  path: FileRouteTypes['to'];

  /**
   * The icon to display before the label
   */
  icon: ReactElement<IconProps>;

  /**
   * Test ID for testing purposes
   */
  testID: string;
} & AccessibilityProps;

const NavItemFrame = styled(Stack, {
  name: 'NavItemFrame',
  group: 'button',
  tag: 'button',
  alignItems: 'center',
  justifyContent: 'flex-start',
  flexDirection: 'row',
  focusable: true,
  cursor: 'pointer',

  accessible: true,
  role: 'button',

  borderWidth: 0,
  borderRadius: '$2',
  gap: '$4',
  paddingVertical: '$2',
  paddingHorizontal: '$4',

  outlineWidth: 2,
  outlineOffset: 2,
  backgroundColor: '$neutral.noChangeTransparent',

  hoverStyle: {
    backgroundColor: '$custom.sideNavigation.hover.fill',
  },

  variants: {
    selected: {
      true: {
        backgroundColor: '$custom.sideNavigation.pressed.fill',
      },
    },
  } as const,
});

const NavItemText = styled(Text, {
  name: 'NavItemText',
  userSelect: 'none',
  textOverflow: 'ellipsis',
  type: '$bodySmall.medium',
  numberOfLines: 1,
  color: '$custom.sideNavigation.enabled.onColor',
});

function getButtonIcon(
  children: ReactElement<IconProps>,
  testID: string | undefined,
) {
  const child = Children.only(children);

  return cloneElement(child, {
    color: '$custom.sideNavigation.enabled.onColor',
    accentColor: '$neutral.noChangeTransparent',
    size: 'S',
    testID,
  });
}

export const NavigationItem = ({
  label,
  path,
  icon,
  testID,
  ...accessibilityProps
}: NavigationItem) => {
  const writingDirection = useDirection();
  const pathname = useRouterState().location.pathname;
  const navigate = useNavigate();

  // Note: we are using `.startsWith` instead of equality check for cases like `/department` and `/department/{id}`
  const isSelected = pathname.startsWith(path);

  return (
    <NavItemFrame
      {...accessibilityProps}
      testID={testID}
      selected={isSelected}
      onPress={() => {
        void navigate({ to: path });
      }}
      direction={writingDirection}
      aria-label={accessibilityProps['aria-label'] ?? label}
      flexShrink={1}
    >
      {getButtonIcon(icon, testID ? `${testID}-icon` : undefined)}
      <Stack flexShrink={1}>
        <NavItemText testID={testID ? `${testID}-label` : undefined}>
          {label}
        </NavItemText>
      </Stack>
    </NavItemFrame>
  );
};
